<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
} from "../composables/useSeo";
import { usePhoneNumber } from "../composables/usePhoneNumber";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();

useSeoBase({
  title: "Gallery | Before & After Junk Removal Projects | Junk Guys MN",
  description:
    "See the amazing transformations from our junk removal projects. Before and after photos of garage cleanouts, basement cleanouts, and more.",
  canonical: "https://junkguysmn.com/gallery",
});

useJsonLd([orgNode(), localBusinessNode()]);
</script>

<template>
  <main class="container mx-auto px-4 py-8">
    <div class="text-center mb-8">
      <h1 class="text-4xl font-bold">Project Gallery</h1>
    </div>
    <p class="text-center text-lg mb-12">
      See the amazing transformations from our junk removal projects
    </p>

    <div class="max-w-6xl mx-auto">
      <!-- Project Categories -->
      <div class="mb-12">
        <h2 class="text-2xl font-semibold mb-6">Project Types</h2>
        <div class="grid md:grid-cols-4 gap-4">
          <button
            class="bg-primary-main text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
          >
            All Projects
          </button>
          <button
            class="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            Garage Cleanouts
          </button>
          <button
            class="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            Basement Cleanouts
          </button>
          <button
            class="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            Estate Cleanouts
          </button>
        </div>
      </div>

      <!-- Gallery Grid -->
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        <!-- Project 1 -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            class="aspect-video bg-gray-200 flex items-center justify-center"
          >
            <span class="text-gray-500">Before Photo</span>
          </div>
          <div class="p-6">
            <h3 class="text-lg font-semibold mb-2">
              Garage Cleanout - Minneapolis
            </h3>
            <p class="text-gray-600 mb-4">
              Complete garage organization and junk removal
            </p>
            <div class="flex justify-between text-sm text-gray-500">
              <span>Before: Cluttered</span>
              <span>After: Organized</span>
            </div>
          </div>
        </div>

        <!-- Project 2 -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            class="aspect-video bg-gray-200 flex items-center justify-center"
          >
            <span class="text-gray-500">After Photo</span>
          </div>
          <div class="p-6">
            <h3 class="text-lg font-semibold mb-2">
              Basement Cleanout - Saint Paul
            </h3>
            <p class="text-gray-600 mb-4">
              Full basement junk removal and cleanup
            </p>
            <div class="flex justify-between text-sm text-gray-500">
              <span>Before: Storage Unit</span>
              <span>After: Clean Space</span>
            </div>
          </div>
        </div>

        <!-- Project 3 -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            class="aspect-video bg-gray-200 flex items-center justify-center"
          >
            <span class="text-gray-500">Before Photo</span>
          </div>
          <div class="p-6">
            <h3 class="text-lg font-semibold mb-2">
              Appliance Removal - Bloomington
            </h3>
            <p class="text-gray-600 mb-4">Old appliance removal and disposal</p>
            <div class="flex justify-between text-sm text-gray-500">
              <span>Before: Old Appliances</span>
              <span>After: Clean Kitchen</span>
            </div>
          </div>
        </div>

        <!-- Project 4 -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            class="aspect-video bg-gray-200 flex items-center justify-center"
          >
            <span class="text-gray-500">After Photo</span>
          </div>
          <div class="p-6">
            <h3 class="text-lg font-semibold mb-2">
              Furniture Removal - Eden Prairie
            </h3>
            <p class="text-gray-600 mb-4">
              Complete furniture removal and space clearing
            </p>
            <div class="flex justify-between text-sm text-gray-500">
              <span>Before: Crowded Room</span>
              <span>After: Open Space</span>
            </div>
          </div>
        </div>

        <!-- Project 5 -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            class="aspect-video bg-gray-200 flex items-center justify-center"
          >
            <span class="text-gray-500">Before Photo</span>
          </div>
          <div class="p-6">
            <h3 class="text-lg font-semibold mb-2">
              Construction Debris - Edina
            </h3>
            <p class="text-gray-600 mb-4">
              Post-construction cleanup and debris removal
            </p>
            <div class="flex justify-between text-sm text-gray-500">
              <span>Before: Debris Everywhere</span>
              <span>After: Clean Site</span>
            </div>
          </div>
        </div>

        <!-- Project 6 -->
        <div class="bg-white rounded-lg shadow-md overflow-hidden">
          <div
            class="aspect-video bg-gray-200 flex items-center justify-center"
          >
            <span class="text-gray-500">After Photo</span>
          </div>
          <div class="p-6">
            <h3 class="text-lg font-semibold mb-2">
              Estate Cleanout - Richfield
            </h3>
            <p class="text-gray-600 mb-4">
              Complete estate cleanout and organization
            </p>
            <div class="flex justify-between text-sm text-gray-500">
              <span>Before: Estate Items</span>
              <span>After: Ready for Sale</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="text-center mt-16">
        <h2 class="text-2xl font-semibold mb-4">
          Ready for Your Transformation?
        </h2>
        <p class="text-lg mb-6">
          Let us help you reclaim your space with professional junk removal
          services.
        </p>
        <div class="flex gap-4 justify-center">
          <a :href="phoneNumberTel" class="btn">Get Free Estimate</a>
          <NuxtLink to="/book" class="btn btn-outline">Book Online</NuxtLink>
        </div>
      </div>
    </div>
  </main>
</template>

